// Contrast helpers for marks painted onto the grid canvas. A generated payroll
// sheet styles its own cells, so a header can arrive as near-black navy, pale
// grey, or a saturated brand color — anything drawn on top has to decide its own
// colors from the cell's actual fill rather than assuming a light background.

import { volcaColor } from "./spreadsheet-theme";

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const NAMED_FALLBACKS: Record<string, Rgb> = {
  white: { r: 255, g: 255, b: 255 },
  black: { r: 0, g: 0, b: 0 },
  transparent: { r: 255, g: 255, b: 255 },
};

/** Parses the color forms the spreadsheet adapter and Glide's theme produce. */
export const parseCssColor = (color: string): Rgb | null => {
  const value = color.trim().toLowerCase();
  if (Object.prototype.hasOwnProperty.call(NAMED_FALLBACKS, value)) {
    return NAMED_FALLBACKS[value] ?? null;
  }

  const hex = value.startsWith("#") ? value.slice(1) : null;
  if (hex && !/^[0-9a-f]+$/.test(hex)) return null;
  if (hex && (hex.length === 3 || hex.length === 4)) {
    const [r, g, b] = [0, 1, 2].map((i) => {
      const digit = hex.charAt(i);
      return Number.parseInt(digit + digit, 16);
    });
    if (r == null || g == null || b == null) return null;
    return [r, g, b].some(Number.isNaN) ? null : { r, g, b };
  }
  if (hex && (hex.length === 6 || hex.length === 8)) {
    const r = Number.parseInt(hex.slice(0, 2), 16);
    const g = Number.parseInt(hex.slice(2, 4), 16);
    const b = Number.parseInt(hex.slice(4, 6), 16);
    return [r, g, b].some(Number.isNaN) ? null : { r, g, b };
  }

  const channels = value.match(/^rgba?\(([^)]+)\)$/)?.[1];
  if (channels) {
    const parts = channels
      .split(/[,\s/]+/)
      .filter(Boolean)
      .map(Number);
    const [r, g, b] = parts;
    if (r == null || g == null || b == null) return null;
    if ([r, g, b].some(Number.isNaN)) return null;
    return { r, g, b };
  }

  return null;
};

const channelLuminance = (channel: number): number => {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};

/** WCAG relative luminance, 0 (black) to 1 (white). */
export const relativeLuminance = ({ r, g, b }: Rgb): number =>
  0.2126 * channelLuminance(r) +
  0.7152 * channelLuminance(g) +
  0.0722 * channelLuminance(b);

/** WCAG contrast ratio, 1 (identical) to 21 (black on white). */
export const contrastRatio = (a: string, b: string): number => {
  const first = parseCssColor(a);
  const second = parseCssColor(b);
  if (!first || !second) return 1;

  const lighter = Math.max(relativeLuminance(first), relativeLuminance(second));
  const darker = Math.min(relativeLuminance(first), relativeLuminance(second));
  return (lighter + 0.05) / (darker + 0.05);
};

/** True when a color is light enough to need dark ink on top of it. */
export const isLightColor = (color: string): boolean => {
  const rgb = parseCssColor(color);
  return rgb ? relativeLuminance(rgb) > 0.45 : true;
};

/**
 * Picks the first candidate that stands off `background` by at least `minRatio`,
 * falling back to plain black or white — whichever the background can support —
 * so a mark is never invisible even on a fill nobody anticipated.
 */
export const pickContrastingColor = (
  background: string,
  candidates: string[],
  minRatio = 2.2,
): string => {
  const winner = candidates.find(
    (candidate) => contrastRatio(candidate, background) >= minRatio,
  );
  if (winner) return winner;

  return isLightColor(background)
    ? volcaColor("--gray-100")
    : volcaColor("--gray-0");
};
